import React, { useState, useEffect, useRef } from 'react';
import { mockData } from '../mock';
import { Card, CardContent } from './ui/card';
import { Check } from 'lucide-react';

const CountUpAnimation = ({ end, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const countRef = useRef(null);

  // Extrahiere Zahl und Suffix
  const endString = end.toString();
  const hasSuffix = endString.includes('+');
  const endValue = parseInt(endString.replace(/\D/g, ''));

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          
          const startTime = Date.now();
          
          const animate = () => {
            const now = Date.now();
            const progress = Math.min((now - startTime) / duration, 1);
            
            // Easing function für smoothere Animation
            const easeOutQuart = 1 - Math.pow(1 - progress, 4);
            const current = Math.floor(easeOutQuart * endValue);
            
            setCount(current);
            
            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              // Stelle sicher, dass die finale Zahl exakt ist
              setCount(endValue);
            }
          };
          
          animate();
        }
      },
      { threshold: 0.3 }
    );

    if (countRef.current) {
      observer.observe(countRef.current);
    }

    return () => {
      if (countRef.current) {
        observer.unobserve(countRef.current);
      }
    };
  }, [endValue, duration, hasAnimated]);

  return (
    <span ref={countRef}>
      {count}{hasSuffix ? '+' : ''}
    </span>
  );
};

const About = () => {
  return (
    <section id="about" className="py-24 bg-[#F5F3ED]">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <span className="inline-block px-6 py-2 bg-white rounded-full text-[#1A3A2E] text-sm font-medium mb-6">
            Über Uns
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#1A3A2E] mb-6 leading-tight">
            Ihr Experte für moderne Wärmepumpensysteme und PV:T Technologie
          </h2>
          <p className="text-xl text-[#1A3A2E]/70 max-w-3xl mx-auto">
            Wir finden für jeden Kunden die passende Lösung.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-24">
          {mockData.features.map((feature, idx) => (
            <Card key={idx} className="border-none shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-white">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-[#1A3A2E] mb-4 leading-tight">
                  {feature.title}
                </h3>
                <p className="text-[#1A3A2E]/70 leading-relaxed">{feature.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mb-24">
          <Card className="border-none shadow-2xl overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              <div className="relative h-96 md:h-full">
                <img
                  src="/novatus-vehicle.jpg"
                  alt="Novatus Energie Fahrzeug"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-12 flex flex-col justify-center bg-white">
                <h3 className="text-3xl font-bold text-[#1A3A2E] mb-6">Unsere Teams sind für Sie unterwegs</h3>
                <p className="text-[#1A3A2E]/70 leading-relaxed mb-6">
                  Mit unseren Fachpartnern sind wir auch in Ihrer Region, für Sie im Einsatz. Unsere Teams bringen modernste Technik und jahrelange Erfahrung direkt zu Ihnen nach Hause.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-[#1A3A2E] flex-shrink-0 mt-1" />
                    <span className="text-[#1A3A2E]/80">Professionelle Fahrzeugausstattung</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-[#1A3A2E] flex-shrink-0 mt-1" />
                    <span className="text-[#1A3A2E]/80">Schnelle Anfahrt im gesamten Bundesgebiet durch regional ansässige Fachunternehmer</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-[#1A3A2E] flex-shrink-0 mt-1" />
                    <span className="text-[#1A3A2E]/80">Kompetente Beratung vor Ort</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-[#1A3A2E] flex-shrink-0 mt-1" />
                    <span className="text-[#1A3A2E]/80">Vom Hersteller zertifizierte Wärmepumpenspezialisten</span>
                  </li>
                </ul>
              </CardContent>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-24">
          {mockData.stats.map((stat, idx) => (
            <Card key={idx} className="border-none shadow-lg bg-white">
              <CardContent className="p-12 text-center">
                <p className="text-lg text-[#1A3A2E]/70 mb-4">{stat.label}</p>
                <p className="text-7xl font-bold text-[#1A3A2E]">
                  <CountUpAnimation 
                    end={stat.value} 
                    duration={6000}
                  />
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mb-16">
          <div className="text-center mb-12">
            <span className="inline-block px-6 py-2 bg-white rounded-full text-[#1A3A2E] text-sm font-medium mb-6">
              Über uns
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#1A3A2E] mb-4">
              Was uns auszeichnet
            </h2>
            <p className="text-lg text-[#1A3A2E]/70 max-w-4xl mx-auto">
              Als moderner Partner des Fachhandwerks gehen wir einen moderneren Weg als herkömmliche Handwerksbetriebe
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-2xl font-bold text-[#1A3A2E] mb-6">Traditionelle Handwerksbetriebe</h3>
              <div className="space-y-6">
                {mockData.comparison.traditional.map((item, idx) => (
                  <Card key={idx} className="border border-red-200 bg-white/50">
                    <CardContent className="p-6">
                      <h4 className="font-bold text-[#1A3A2E] mb-2">{item.title}</h4>
                      <p className="text-[#1A3A2E]/70 text-sm">{item.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-[#1A3A2E] mb-6">Novatus Energie</h3>
              <div className="space-y-6">
                {mockData.comparison.modern.map((item, idx) => (
                  <Card key={idx} className="border border-green-200 bg-white shadow-md">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-3">
                        <div className="w-6 h-6 rounded-full bg-[#1A3A2E] flex items-center justify-center flex-shrink-0 mt-1">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <h4 className="font-bold text-[#1A3A2E] mb-2">{item.title}</h4>
                          <p className="text-[#1A3A2E]/70 text-sm">{item.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="text-center">
          <p className="text-xl text-[#1A3A2E] max-w-4xl mx-auto italic">
            Wir wissen, dass jedes Projekt anders ist und nehmen uns genug Zeit, um für Sie die beste Lösung zu finden
          </p>
        </div>
      </div>
    </section>
  );
};

export default About;